import { Link } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import clsx from 'clsx';
import Icon from './Icon.jsx';
import { db } from '../db/index.js';
import { useSyncStore } from '../sync/store.js';
import { useNow } from '../hooks/useNow.js';
import styles from './SyncIndicator.module.scss';

export function timeAgo(ms, now = Date.now()) {
  if (!ms) return 'never';
  const s = Math.round((now - ms) / 1000);
  if (s < 10) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(ms).toLocaleDateString();
}

export function useSyncSummary() {
  const status = useSyncStore((s) => s.status);
  const lastSyncAt = useSyncStore((s) => s.lastSyncAt);
  const integrity = useSyncStore((s) => s.integrity);
  const pending = useLiveQuery(() => db.outbox.count(), [], 0);
  const now = useNow(15000);

  let icon = 'cloud';
  let label;
  let tone;
  switch (status) {
    case 'syncing':
      icon = 'sync';
      label = 'Syncing…';
      tone = 'busy';
      break;
    case 'offline':
      icon = 'cloudOff';
      label = pending ? `Offline · ${pending} change${pending === 1 ? '' : 's'} saved on device` : 'Offline · all saved on device';
      tone = 'warn';
      break;
    case 'error':
      icon = 'warning';
      label = pending ? `Sync issue · ${pending} saved on device, retrying` : 'Sync issue · retrying';
      tone = 'warn';
      break;
    case 'idle':
      label = pending ? `${pending} change${pending === 1 ? '' : 's'} waiting` : `Synced ${timeAgo(lastSyncAt, now)}`;
      tone = pending ? 'busy' : integrity === 'mismatch' ? 'warn' : 'ok';
      break;
    default:
      icon = 'device';
      label = 'On this device only';
      tone = 'local';
  }
  return { status, icon, label, tone, pending };
}

export default function SyncIndicator({ compact = false }) {
  const { icon, label, tone, status } = useSyncSummary();
  return (
    <Link
      to="/settings#sync"
      className={clsx(styles.indicator, styles[tone], compact && styles.compact)}
      title={label}
      aria-label={`Sync status: ${label}`}
    >
      <span className={clsx(styles.dot, status === 'syncing' && styles.spin)}>
        <Icon name={icon} size={18} />
      </span>
      {compact ? null : <span className={styles.label}>{label}</span>}
    </Link>
  );
}
